import {
    Box,
    Button,
    Checkbox,
    Flex,
    Icon,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Stack,
    Text,
    Tooltip,
    UnorderedList,
    ListItem
} from "@chakra-ui/react";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useMutation} from "@apollo/client";
import Upload, {UploadFileMetadata} from "../upload/Upload";
import {UploadResult} from "../uploadQueue/types";
import {SoundFile, SoundFileLibraryEntry} from "../types";
import {BiHistory, BiLink, BiUnlink} from "react-icons/bi";
import {firebaseApp} from "../../firebaseClient";
import {deleteObject, getBlob, getDownloadURL, getStorage, ref} from "firebase/storage";
import {purgeSoundFilesMutation} from "./soundFileQueries";
import {useConfirmDialog} from "../common/ConfirmDialogProvider";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {refreshAreaStatisticsMutation} from "../area/areaQueries";
import {getTransitionStoredFilePaths} from "../upload/uploadPaths";

export type ExistingSoundOption = {
    sound: SoundFile
    spotNames: string[]
    libraryEntry?: SoundFileLibraryEntry
}

type Props = {
    isOpen: boolean
    onClose: () => void
    mode: "slot" | "library"
    existingSoundOptions: ExistingSoundOption[]
    isLoading: boolean
    selectedExistingFile?: string
    onSelectExistingFile?: (value: string) => void
    onAddExisting?: () => void
    onUpload: (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => void
    registerUploadedFile?: (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => Promise<void> | void
    isReplace?: boolean
    allowMultipleUpload?: boolean
    onAfterDelete?: () => void
    onAfterUploadBatch?: () => void
    enqueueUploads: (files: File[], onCompleted: (result: UploadResult) => Promise<void> | void) => void
}

const SoundFileLibraryDialog = ({
    isOpen,
    onClose,
    mode,
    existingSoundOptions,
    isLoading,
    selectedExistingFile,
    onSelectExistingFile,
    onAddExisting,
    onUpload,
    registerUploadedFile,
    isReplace,
    allowMultipleUpload,
    onAfterDelete,
    onAfterUploadBatch,
    enqueueUploads
}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const {confirm} = useConfirmDialog();
    const [purgeSoundFiles] = useMutation(purgeSoundFilesMutation);
    const [refreshAreaStatistics] = useMutation(refreshAreaStatisticsMutation);
    const [selectedLibraryFiles, setSelectedLibraryFiles] = useState<Set<string>>(new Set());
    const [selectedLibraryPreview, setSelectedLibraryPreview] = useState<string | undefined>(undefined);
    const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined);
    const [loadedFileName, setLoadedFileName] = useState<string | undefined>(undefined);
    const [usageFilter, setUsageFilter] = useState<"all" | "used" | "previously" | "unused">("all");
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const selectedExistingRef = useRef<HTMLDivElement | null>(null);
    const selectedLibraryRef = useRef<HTMLDivElement | null>(null);
    const didScrollExistingRef = useRef<boolean>(false);
    const didScrollLibraryRef = useRef<boolean>(false);
    const [isDropTarget, setIsDropTarget] = useState<boolean>(false);
    const dragCounterRef = useRef<number>(0);
    const pendingSelectionRef = useRef<string | undefined>(undefined);
    const pendingScrollFileRef = useRef<string | undefined>(undefined);
    const areaId = appContext.getArea()?.id;

    const refreshStatistics = useCallback(async () => {
        if (!areaId) {
            return;
        }
        try {
            await refreshAreaStatistics({
                variables: {
                    areaId: areaId
                }
            });
        } catch (error) {
            console.warn("Unable to refresh area statistics", error);
        }
    }, [areaId, refreshAreaStatistics]);

    const sortedOptions = useMemo(() => {
        return existingSoundOptions
            .filter((option) => !option.libraryEntry?.purgedAt)
            .sort((a, b) => (a.sound.originalFileName || "").localeCompare(b.sound.originalFileName || ""));
    }, [existingSoundOptions]);

    const selectedFileName = mode === "slot" ? selectedExistingFile : selectedLibraryPreview;

    const selectedOption = useMemo(() => {
        if (!selectedFileName) {
            return undefined;
        }
        return sortedOptions.find((option) => option.sound.fileName === selectedFileName);
    }, [selectedFileName, sortedOptions]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setAudioUrl(undefined);
        setLoadedFileName(undefined);
    }, [selectedFileName, isOpen]);

    useEffect(() => {
        if (isOpen) {
            return;
        }
        setSelectedLibraryPreview(undefined);
        setSelectedLibraryFiles(new Set());
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            didScrollExistingRef.current = false;
            didScrollLibraryRef.current = false;
            setIsDropTarget(false);
            dragCounterRef.current = 0;
            pendingSelectionRef.current = undefined;
            pendingScrollFileRef.current = undefined;
        }
    }, [isOpen]);

    useEffect(() => {
        const target = pendingSelectionRef.current;
        if (!target || !onSelectExistingFile) {
            return;
        }
        const exists = existingSoundOptions.some((option) => option.sound.fileName === target);
        if (exists) {
            pendingSelectionRef.current = undefined;
            pendingScrollFileRef.current = target;
            onSelectExistingFile(target);
        }
    }, [existingSoundOptions, onSelectExistingFile]);

    useEffect(() => {
        if (!isOpen || mode !== "library") {
            return;
        }
        didScrollLibraryRef.current = false;
    }, [isOpen, mode, usageFilter]);

    const isOptionUsed = useCallback((option: ExistingSoundOption) => {
        return option.spotNames && option.spotNames.some((name) => name && name.trim().length > 0);
    }, []);

    const getOptionUsageStatus = useCallback((option: ExistingSoundOption) => {
        if (isOptionUsed(option)) {
            return "used";
        }
        if (option.libraryEntry?.lastUsedPublishedAt) {
            return "previously";
        }
        return "unused";
    }, [isOptionUsed]);

    const toggleUsageFilter = () => {
        setUsageFilter((prev) => {
            if (prev === "all") {
                return "used";
            }
            if (prev === "used") {
                return "previously";
            }
            if (prev === "previously") {
                return "unused";
            }
            return "all";
        });
    };

    useEffect(() => {
        if (mode !== "library" || !selectedLibraryPreview) {
            return;
        }
        const option = sortedOptions.find((item) => item.sound.fileName === selectedLibraryPreview);
        if (!option) {
            return;
        }
        const status = getOptionUsageStatus(option);
        if (usageFilter === "used" && status !== "used") {
            setSelectedLibraryPreview(undefined);
        }
        if (usageFilter === "previously" && status !== "previously") {
            setSelectedLibraryPreview(undefined);
        }
        if (usageFilter === "unused" && status !== "unused") {
            setSelectedLibraryPreview(undefined);
        }
    }, [getOptionUsageStatus, mode, selectedLibraryPreview, sortedOptions, usageFilter]);

    const selectFile = (fileName: string) => {
        if (mode === "slot") {
            onSelectExistingFile && onSelectExistingFile(fileName);
            return;
        }
        setSelectedLibraryPreview(fileName);
    };

    const formatBytes = (value?: number) => {
        if (!value || !Number.isFinite(value)) {
            return undefined;
        }
        const units = ["B", "KB", "MB", "GB"];
        let unitIndex = 0;
        let size = value;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size = size / 1024;
            unitIndex += 1;
        }
        return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    };

    const formatDuration = (value?: number) => {
        if (!value || !Number.isFinite(value)) {
            return undefined;
        }
        const totalSeconds = Math.round(value / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    const selectedFileInfo = useMemo(() => {
        if (!selectedOption) {
            return undefined;
        }
        const libraryEntry = selectedOption.libraryEntry;
        const sound = selectedOption.sound;
        return {
            fileName: libraryEntry?.objectId || sound.fileName,
            originalFileName: libraryEntry?.originalFileName || sound.originalFileName || sound.fileName,
            filePath: libraryEntry?.filePath || sound.filePath || "SoundFiles",
            uploadedBy: libraryEntry?.uploadedBy,
            mimeType: libraryEntry?.mimeType,
            sizeBytes: libraryEntry?.sizeBytes,
            durationMs: libraryEntry?.durationMs,
            lastUsedPublishedAt: libraryEntry?.lastUsedPublishedAt,
            lastUsedPublishedVersionName: libraryEntry?.lastUsedPublishedVersionName,
            usedIn: (selectedOption.spotNames || []).filter((name) => name && name.trim().length > 0)
        };
    }, [selectedOption]);

    const preloadAudio = useCallback(async () => {
        const fileInfo = selectedFileInfo;
        if (!fileInfo?.fileName) {
            return;
        }
        if (loadedFileName === fileInfo.fileName && audioUrl) {
            return;
        }
        try {
            const storage = getStorage(firebaseApp);
            const storageRef = ref(storage, `${fileInfo.filePath}/${fileInfo.fileName}`);
            const url = await getDownloadURL(storageRef);
            setAudioUrl(url);
            setLoadedFileName(fileInfo.fileName);
            if (audioRef.current) {
                audioRef.current.src = url;
            }
        } catch (error) {
            console.warn("Unable to load audio preview", error);
        }
    }, [audioUrl, loadedFileName, selectedFileInfo]);

    const purgeSelectedFiles = useCallback(async (selectedOptions: ExistingSoundOption[]) => {
        if (selectedOptions.length === 0 || !areaId) {
            return;
        }
        const previouslyUsedCount = selectedOptions.filter((option) => getOptionUsageStatus(option) === "previously").length;
        const unusedCount = selectedOptions.length - previouslyUsedCount;
        const bodyParts: React.ReactNode[] = [];
        if (previouslyUsedCount > 0) {
            bodyParts.push(
                <Text key={"warn"} fontSize={"sm"} color={"red.600"} fontWeight={"semibold"}>
                    Warning: {previouslyUsedCount} of these file{previouslyUsedCount === 1 ? " was" : "s were"} used in
                    previously published versions. Purging them removes the audio from storage — those old versions
                    will no longer play these sounds and cannot be restored.
                </Text>
            );
        }
        bodyParts.push(
            <Text key={"summary"} fontSize={"sm"}>
                {unusedCount > 0 && previouslyUsedCount > 0
                    ? `Purge ${unusedCount} unused and ${previouslyUsedCount} previously used file${selectedOptions.length === 1 ? "" : "s"}?`
                    : previouslyUsedCount > 0
                        ? `Purge ${previouslyUsedCount} previously used file${previouslyUsedCount === 1 ? "" : "s"}?`
                        : `Purge ${unusedCount} unused file${unusedCount === 1 ? "" : "s"}?`}
            </Text>
        );
        const approved = await confirm({
            title: "Purge sound files",
            body: <Stack spacing={3}>{bodyParts}</Stack>,
            confirmText: "Purge",
            isDestructive: true
        });
        if (!approved) {
            return;
        }

        const objectIds = selectedOptions
            .map((option) => option.libraryEntry?.objectId || option.sound.fileName)
            .filter((value): value is string => !!value);
        if (objectIds.length === 0) {
            return;
        }

        try {
            await purgeSoundFiles({
                variables: {
                    areaId: areaId,
                    objectIds: objectIds
                }
            });
        } catch (error) {
            console.warn("Unable to purge sound file library entries", error);
            return;
        }

        const storage = getStorage(firebaseApp);
        await Promise.all(selectedOptions.map(async (option) => {
            const fileName = option.libraryEntry?.objectId || option.sound.fileName;
            const filePath = option.libraryEntry?.filePath || option.sound.filePath || "SoundFiles";
            if (!fileName) {
                return;
            }
            try {
                const candidatePaths = getTransitionStoredFilePaths(areaId, filePath);
                await Promise.all(candidatePaths.map(async (candidatePath) => {
                    try {
                        await deleteObject(ref(storage, `${candidatePath}/${fileName}`));
                    } catch (error: any) {
                        if (error?.code !== "storage/object-not-found") {
                            throw error;
                        }
                    }
                }));
            } catch (error) {
                console.warn("Unable to delete sound file from storage; library entry is marked purged and can be reconciled later", error);
            }
        }));

        const nextSelected = new Set(selectedLibraryFiles);
        objectIds.forEach((fileName) => nextSelected.delete(fileName));
        setSelectedLibraryFiles(nextSelected);
        if (selectedLibraryPreview && objectIds.includes(selectedLibraryPreview)) {
            setSelectedLibraryPreview(undefined);
        }

        await refreshStatistics();

        onAfterDelete && onAfterDelete();
    }, [areaId, getOptionUsageStatus, onAfterDelete, purgeSoundFiles, refreshStatistics, selectedLibraryFiles, selectedLibraryPreview, confirm]);

    const [isDownloading, setIsDownloading] = useState(false);

    const downloadSelectedFiles = useCallback(async (selectedOptions: ExistingSoundOption[]) => {
        if (selectedOptions.length === 0) {
            return;
        }
        setIsDownloading(true);
        try {
            const storage = getStorage(firebaseApp);
            if (selectedOptions.length === 1) {
                const option = selectedOptions[0];
                const fileName = option.libraryEntry?.objectId || option.sound.fileName;
                const filePath = option.libraryEntry?.filePath || option.sound.filePath || "SoundFiles";
                const originalName = option.sound.originalFileName || option.libraryEntry?.originalFileName || fileName || "audio";
                if (!fileName) return;
                const storageRef = ref(storage, `${filePath}/${fileName}`);
                const blob = await getBlob(storageRef);
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = originalName;
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                const JSZip = (await import("jszip")).default;
                const zip = new JSZip();
                const usedNames = new Map<string, number>();
                await Promise.all(selectedOptions.map(async (option) => {
                    const fileName = option.libraryEntry?.objectId || option.sound.fileName;
                    const filePath = option.libraryEntry?.filePath || option.sound.filePath || "SoundFiles";
                    const originalName = option.sound.originalFileName || option.libraryEntry?.originalFileName || fileName || "audio";
                    if (!fileName) return;
                    try {
                        const storageRef = ref(storage, `${filePath}/${fileName}`);
                        const blob = await getBlob(storageRef);
                        let zipName = originalName;
                        const count = usedNames.get(zipName) || 0;
                        if (count > 0) {
                            const dotIndex = zipName.lastIndexOf(".");
                            const base = dotIndex > 0 ? zipName.substring(0, dotIndex) : zipName;
                            const ext = dotIndex > 0 ? zipName.substring(dotIndex) : "";
                            zipName = `${base} (${count})${ext}`;
                        }
                        usedNames.set(originalName, count + 1);
                        zip.file(zipName, blob);
                    } catch (error) {
                        console.warn(`Unable to download ${originalName}`, error);
                    }
                }));
                const zipBlob = await zip.generateAsync({type: "blob"});
                const link = document.createElement("a");
                link.href = URL.createObjectURL(zipBlob);
                link.download = "sound-files.zip";
                link.click();
                URL.revokeObjectURL(link.href);
            }
        } catch (error) {
            console.error("Download failed", error);
        } finally {
            setIsDownloading(false);
        }
    }, []);

    const dataTransferHasFiles = (dataTransfer: DataTransfer | null) => {
        if (!dataTransfer) {
            return false;
        }
        if (dataTransfer.files && dataTransfer.files.length > 0) {
            return true;
        }
        return Array.from(dataTransfer.types || []).includes("Files");
    };

    const handleDialogDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        if (!enqueueUploads || !dataTransferHasFiles(event.dataTransfer)) {
            return;
        }
        event.stopPropagation();
        dragCounterRef.current += 1;
        setIsDropTarget(true);
    };

    const handleDialogDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (!enqueueUploads || !dataTransferHasFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "copy";
    };

    const handleDialogDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        if (!enqueueUploads) {
            return;
        }
        event.stopPropagation();
        const nextTarget = event.relatedTarget as Node | null;
        if (nextTarget && event.currentTarget.contains(nextTarget)) {
            return;
        }
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
            setIsDropTarget(false);
        }
    };

    const handleUploadComplete = async (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        if (mode === "slot" && isReplace && registerUploadedFile) {
            pendingSelectionRef.current = fileName;
            await registerUploadedFile(originalFileName, fileName, filePath, metadata);
            return;
        }
        onUpload(originalFileName, fileName, filePath, metadata);
    };

    const handleDialogDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (!enqueueUploads) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = 0;
        setIsDropTarget(false);
        const files = Array.from(event.dataTransfer.files || []);
        if (files.length === 0) {
            return;
        }
        enqueueUploads(files, async (result) => {
            await handleUploadComplete(result.originalFileName, result.fileName, result.filePath, result.metadata);
            await refreshStatistics();
            onAfterUploadBatch && onAfterUploadBatch();
        });
    };

    useEffect(() => {
        if (!isOpen || !selectedOption || !selectedFileName) {
            return;
        }
        preloadAudio().catch(() => null);
    }, [isOpen, preloadAudio, selectedFileName, selectedOption]);

    useEffect(() => {
        if (!isOpen || !selectedExistingFile || !selectedExistingRef.current) {
            return;
        }
        const isPendingScrollTarget = pendingScrollFileRef.current === selectedExistingFile;
        if (didScrollExistingRef.current && !isPendingScrollTarget) {
            return;
        }
        selectedExistingRef.current.scrollIntoView({block: "nearest"});
        didScrollExistingRef.current = true;
        if (isPendingScrollTarget) {
            pendingScrollFileRef.current = undefined;
        }
    }, [isOpen, selectedExistingFile, sortedOptions.length]);

    useEffect(() => {
        if (!isOpen || didScrollLibraryRef.current || !selectedLibraryPreview || !selectedLibraryRef.current) {
            return;
        }
        selectedLibraryRef.current.scrollIntoView({block: "nearest"});
        didScrollLibraryRef.current = true;
    }, [isOpen, selectedLibraryPreview, sortedOptions.length, usageFilter]);

    const renderSoundOption = (option: ExistingSoundOption, control: React.ReactNode) => {
        const {sound} = option;
        const status = getOptionUsageStatus(option);
        const statusIcon = status === "used" ? BiLink : status === "previously" ? BiHistory : BiUnlink;
        const statusColor = status === "used" ? "blue.500" : status === "previously" ? "yellow.600" : "orange.500";
        const statusLabel = status === "used"
            ? "Used in Draft/Published"
            : status === "previously"
                ? "Previously used in a published version"
                : "Never used in published versions";
        return (
            <Box>
                <Box display={"flex"} alignItems={"flex-start"} columnGap={"8px"}>
                    {control}
                    <Box>
                        <Box display={"flex"} alignItems={"center"} columnGap={"6px"}>
                            <Text fontWeight={"semibold"}>{sound.originalFileName || sound.fileName}</Text>
                            <Tooltip label={statusLabel} placement="top" hasArrow>
                                <Box display={"inline-flex"} alignItems={"center"} color={statusColor}>
                                    <Icon as={statusIcon} boxSize={4} aria-label={statusLabel}/>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    const formatPublishedUsage = (lastUsedPublishedAt?: number, versionName?: string) => {
        if (!lastUsedPublishedAt) {
            return "Never used in published versions";
        }
        const dateLabel = new Date(lastUsedPublishedAt).toLocaleString();
        if (versionName) {
            return `Previously used in Published "${versionName}" at ${dateLabel}`;
        }
        return `Previously used in Published at ${dateLabel}`;
    };

    const renderDetailsPanel = () => {
        const fileInfo = selectedFileInfo;
        if (!fileInfo || !fileInfo.fileName) {
            return <Box />;
        }

        const formattedSize = formatBytes(fileInfo.sizeBytes);
        const formattedDuration = formatDuration(fileInfo.durationMs);
        const usageLabel = fileInfo.usedIn.length > 0
            ? fileInfo.usedIn
            : [formatPublishedUsage(fileInfo.lastUsedPublishedAt, fileInfo.lastUsedPublishedVersionName)];

        return (
            <Box
                borderLeft={"1px solid"}
                borderColor={"gray.200"}
                paddingLeft={4}
                position={"sticky"}
                top={0}
                alignSelf={"start"}
                maxH={"65vh"}
                overflowY={"auto"}
            >
                <Stack spacing={4} paddingRight={2} paddingBottom={2}>
                    <Stack spacing={2}>
                        <Text fontWeight={"semibold"}>File details</Text>
                        <Text fontSize={"sm"}><strong>Original:</strong> {fileInfo.originalFileName || "Unknown"}</Text>
                        <Text fontSize={"sm"}><strong>Stored name:</strong> {fileInfo.fileName}</Text>
                        <Text fontSize={"sm"}><strong>Folder:</strong> {fileInfo.filePath || "SoundFiles"}</Text>
                        {fileInfo.uploadedBy ? <Text fontSize={"sm"}><strong>Uploaded by:</strong> {fileInfo.uploadedBy}</Text> : null}
                        {fileInfo.mimeType ? <Text fontSize={"sm"}><strong>Type:</strong> {fileInfo.mimeType}</Text> : null}
                        {formattedSize ? <Text fontSize={"sm"}><strong>Size:</strong> {formattedSize}</Text> : null}
                        {formattedDuration ? <Text fontSize={"sm"}><strong>Duration:</strong> {formattedDuration}</Text> : null}
                        <Box>
                            <Text fontSize={"sm"} fontWeight={"semibold"}>Used in</Text>
                            <UnorderedList marginStart={5} spacing={1}>
                                {usageLabel.map((usageLine) => (
                                    <ListItem key={usageLine}>
                                        <Text fontSize={"sm"}>{usageLine}</Text>
                                    </ListItem>
                                ))}
                            </UnorderedList>
                        </Box>
                    </Stack>
                    <Stack spacing={3}>
                        <Text fontWeight={"semibold"}>Preview</Text>
                        <audio ref={audioRef} controls src={audioUrl} style={{width: "100%"}}/>
                    </Stack>
                </Stack>
            </Box>
        );
    };

    const renderSelectExisting = () => {
        if (isLoading) {
            return <Text>Loading sound files...</Text>;
        }
        if (sortedOptions.length === 0) {
            return <Text>No other sound files available</Text>;
        }
        return (
            <RadioGroup onChange={onSelectExistingFile} value={selectedExistingFile} height={"100%"}>
                <Box
                    display={"grid"}
                    gridTemplateColumns={"minmax(0, 1fr) 520px"}
                    columnGap={4}
                    height={"100%"}
                    minH={0}
                    alignItems={"start"}
                >
                    <Stack spacing={2} overflowY={"auto"} minH={0} paddingRight={1} height={"100%"}>
                        {sortedOptions.map((option) => {
                            const fileName = option.sound.fileName || "";
                            const isSelected = selectedExistingFile === fileName;
                            return (
                                <Box
                                    key={fileName}
                                    ref={isSelected ? selectedExistingRef : undefined}
                                    padding={2}
                                    borderRadius={"md"}
                                    borderWidth={"1px"}
                                    borderColor={isSelected ? "blue.300" : "transparent"}
                                    backgroundColor={isSelected ? "blue.50" : "transparent"}
                                    cursor={"pointer"}
                                    onClick={() => selectFile(fileName)}
                                >
                                    {renderSoundOption(option, <Radio value={fileName}/>)}
                                </Box>
                            );
                        })}
                    </Stack>
                    {renderDetailsPanel()}
                </Box>
            </RadioGroup>
        );
    };

    const renderLibraryList = () => {
        if (isLoading) {
            return <Text>Loading sound files...</Text>;
        }
        if (sortedOptions.length === 0) {
            return <Text>No sound files found for this space.</Text>;
        }
        const filteredOptions = usageFilter === "all"
            ? sortedOptions
            : sortedOptions.filter((option) => getOptionUsageStatus(option) === usageFilter);
        const selectedOptions = filteredOptions.filter((option) => {
            const fileName = option.sound.fileName;
            return !!fileName && selectedLibraryFiles.has(fileName);
        });
        const hasSelection = selectedOptions.length > 0;
        const hasBlockedSelection = selectedOptions.some((option) => getOptionUsageStatus(option) === "used");
        const canPurgeSelected = hasSelection && !hasBlockedSelection;
        const visibleFileNames = filteredOptions
            .map((option) => option.sound.fileName)
            .filter((fileName): fileName is string => !!fileName);
        const allSelected = visibleFileNames.length > 0 && visibleFileNames.every((fileName) => selectedLibraryFiles.has(fileName));
        const filterIcon = usageFilter === "unused"
            ? BiUnlink
            : usageFilter === "previously"
                ? BiHistory
                : BiLink;
        const filterColor = usageFilter === "all"
            ? "gray.600"
            : usageFilter === "unused"
                ? "orange.500"
                : usageFilter === "previously"
                    ? "yellow.600"
                    : "blue.500";
        const filterTooltip = usageFilter === "all"
            ? "Show used files"
            : usageFilter === "used"
                ? "Show previously used files"
                : usageFilter === "previously"
                    ? "Show unused files"
                    : "Show all files";
        return (
            <Box
                display={"grid"}
                gridTemplateColumns={"minmax(0, 1fr) 520px"}
                columnGap={4}
                height={"100%"}
                minH={0}
                alignItems={"start"}
                overflow={"hidden"}
            >
                <Box display={"flex"} flexDirection={"column"} minH={0} height={"100%"}>
                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} paddingBottom={2} paddingLeft={2}>
                        <Box display={"flex"} alignItems={"center"} columnGap={3}>
                            <Checkbox
                                isChecked={allSelected}
                                onChange={() => {
                                    if (allSelected) {
                                        setSelectedLibraryFiles(new Set());
                                        return;
                                    }
                                    setSelectedLibraryFiles(new Set(visibleFileNames));
                                }}
                            >
                                Select / deselect all
                            </Checkbox>
                            <Text fontSize={"sm"} color={"gray.600"}>
                                {selectedOptions.length} selected
                            </Text>
                        </Box>
                        <Box display={"flex"} alignItems={"center"} columnGap={2}>
                            <Tooltip label={filterTooltip} hasArrow>
                                <IconButton
                                    aria-label={filterTooltip}
                                    icon={<Icon as={filterIcon} boxSize={4}/>}
                                    size={"sm"}
                                    variant={"ghost"}
                                    color={filterColor}
                                    onClick={toggleUsageFilter}
                                />
                            </Tooltip>
                            <Button
                                size={"sm"}
                                isDisabled={!hasSelection || isDownloading}
                                isLoading={isDownloading}
                                loadingText={"Downloading..."}
                                onClick={() => downloadSelectedFiles(selectedOptions)}
                            >
                                Download selected
                            </Button>
                            <Tooltip
                                label={hasSelection && hasBlockedSelection ? "One or more selected files are currently in use and cannot be purged." : ""}
                                isDisabled={!hasSelection || !hasBlockedSelection}
                                hasArrow
                            >
                                <Button size={"sm"} isDisabled={!canPurgeSelected} onClick={() => purgeSelectedFiles(selectedOptions)}>
                                    Purge selected
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Stack spacing={2} overflowY={"auto"} minH={0} paddingRight={1} flex={1}>
                        {filteredOptions.map((option) => {
                            const fileName = option.sound.fileName || "";
                            const isChecked = selectedLibraryFiles.has(fileName);
                            const isSelected = selectedLibraryPreview === fileName;
                            return (
                                <Box
                                    key={fileName}
                                    ref={isSelected ? selectedLibraryRef : undefined}
                                    padding={2}
                                    borderRadius={"md"}
                                    borderWidth={"1px"}
                                    borderColor={isSelected ? "blue.300" : "transparent"}
                                    backgroundColor={isSelected ? "blue.50" : "transparent"}
                                    cursor={"pointer"}
                                    onClick={() => selectFile(fileName)}
                                >
                                    {renderSoundOption(option, (
                                        <Checkbox
                                            isChecked={isChecked}
                                            onChange={(event) => {
                                                const next = new Set(selectedLibraryFiles);
                                                if (event.target.checked) {
                                                    next.add(fileName);
                                                } else {
                                                    next.delete(fileName);
                                                }
                                                setSelectedLibraryFiles(next);
                                            }}
                                        />
                                    ))}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
                {renderDetailsPanel()}
            </Box>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size={"6xl"}>
            <ModalOverlay/>
            <ModalContent
                maxW={"90vw"}
                height={"90vh"}
                position={"relative"}
                onDragEnter={handleDialogDragEnter}
                onDragOver={handleDialogDragOver}
                onDragLeave={handleDialogDragLeave}
                onDrop={handleDialogDrop}
            >
                <ModalHeader>Manage sound file</ModalHeader>
                <ModalCloseButton/>
                <ModalBody height={"100%"} overflowY={"hidden"}>
                    <Box height={"100%"} minH={0}>
                        {mode === "slot" ? renderSelectExisting() : renderLibraryList()}
                    </Box>
                </ModalBody>
                <ModalFooter borderTopWidth={"1px"} borderColor={"gray.100"}>
                    <Flex flex={1} alignItems={"center"} columnGap={3}>
                        <Upload
                            allowMultiple={mode === "library" ? allowMultipleUpload : false}
                            onFinishUpload={handleUploadComplete}
                            onAllUploadsFinished={async () => {
                                await refreshStatistics();
                                onAfterUploadBatch && onAfterUploadBatch();
                            }}
                            enqueueUploads={enqueueUploads}
                        />
                    </Flex>
                    {mode === "slot" ? (
                        <Button colorScheme='blue' mr={2} onClick={onAddExisting} isDisabled={!selectedExistingFile}>
                            {isReplace ? "Set" : "Add"}
                        </Button>
                    ) : null}
                    <Button variant='ghost' onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
                {isDropTarget ? (
                    <Box
                        position={"absolute"}
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bg={"blue.50"}
                        opacity={0.92}
                        border={"3px dashed"}
                        borderColor={"blue.400"}
                        borderRadius={"md"}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        pointerEvents={"none"}
                        zIndex={5}
                    >
                        <Text fontSize={"2xl"} color={"blue.700"} fontWeight={"bold"}>
                            Drop files to upload
                        </Text>
                    </Box>
                ) : null}
            </ModalContent>
        </Modal>
    );
};

export default SoundFileLibraryDialog;
